import ClockIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { ANALYTICS_SUBJECT_ID_SUPPORT_TICKET_LINK, EVENT_CONTAINER_SUPPORT_APPLISTING_TAB, LinkClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { ExternalLink, SectionHeading } from '@atlassian/mpac-ui';
import { humanizeSupportTimings } from '@atlassian/mpac-utils';
import React from 'react';
import { TEST_ID } from '../../../../../constants';
import { Section } from '../../../../Section';
import { ContactDetailsList, ContactItem, SupportOnHolidayIndicator, SupportOpenIndicator, SupportOpenIndicatorLabel } from '../styles';
import { SupportAvailability as SupportAvailabilityValues, SupportResourcesLink } from '../types';
import { getPartnerSupportAvailability } from '../utils';
export const makeMailToLink = emailId => "mailto:".concat(emailId);
export const DATA_TEST_ID = {
  emailId: 'app-listing__support-link__email-id',
  phoneNumber: 'app-listing__support-link__phone-number',
  statusPageUrl: 'app-listing__support-link__status-page-url',
  supportTimings: 'app-listing__support-contact__availability-timings'
};
export const SupportRequest = props => {
  const fireAnalyticsEvent = useAnalyticsEvents();
  const {
    supportTicketSystemUrl,
    issueTrackerUrl,
    websiteUrl
  } = props;
  const onSupportRequestLinkClick = (supportResourcesLinkType, text) => {
    props.onSupportLinkClick(supportResourcesLinkType);
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_SUPPORT_TICKET_LINK,
      eventContainer: EVENT_CONTAINER_SUPPORT_APPLISTING_TAB,
      attributes: {
        linkText: text
      }
    });
  };
  if (supportTicketSystemUrl) {
    const linkText = 'Submit a support request';
    return /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(ExternalLink, {
      href: supportTicketSystemUrl,
      onClick: () => onSupportRequestLinkClick(SupportResourcesLink.ISSUE_TRACKER, linkText),
      title: "Request support for app"
    }, linkText));
  }
  if (issueTrackerUrl) {
    const linkText = 'Submit a support request';
    return /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(ExternalLink, {
      href: issueTrackerUrl,
      onClick: () => onSupportRequestLinkClick(SupportResourcesLink.ISSUE_TRACKER, linkText),
      title: "Request support for app"
    }, linkText));
  }
  if (websiteUrl) {
    const linkText = 'Go to vendor support website';
    return /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(ExternalLink, {
      href: websiteUrl,
      onClick: () => onSupportRequestLinkClick(SupportResourcesLink.SUPPORT_WEBSITE, linkText),
      title: "Visit website for support"
    }, linkText));
  }
  return null;
};
export const SupportAvailability = props => {
  const {
    availability
  } = props;
  if (!availability) return null;
  const availabilityType = getPartnerSupportAvailability({
    availability,
    localTime: Date.now()
  });
  if (availabilityType === SupportAvailabilityValues.Open) {
    return /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(SupportOpenIndicator, null, /*#__PURE__*/React.createElement(ClockIcon, {
      label: "Support hours"
    }), /*#__PURE__*/React.createElement(SupportOpenIndicatorLabel, null, "Open now")));
  } else if (availabilityType === SupportAvailabilityValues.OnHoliday) {
    return /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(SupportOnHolidayIndicator, null, /*#__PURE__*/React.createElement(ClockIcon, {
      label: "Support hours"
    }), /*#__PURE__*/React.createElement(SupportOpenIndicatorLabel, {
      title: "Support is currently unavailable due to a holiday"
    }, "Closed for holiday")));
  }
  return null;
};
const SupportContact = props => {
  const {
    supportDetails
  } = props;
  const {
    contact: {
      emailId,
      phoneNumber,
      supportTicketSystemUrl,
      issueTrackerUrl,
      websiteUrl,
      statusPageUrl
    },
    isSupported,
    availability
  } = supportDetails;
  const hasIssueTrackingResources = issueTrackerUrl || statusPageUrl || supportTicketSystemUrl || websiteUrl;
  if (!isSupported && !hasIssueTrackingResources) return null;
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(SectionHeading, null, "Contact"), /*#__PURE__*/React.createElement(ContactDetailsList, null, isSupported && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SupportAvailability, {
    availability: availability
  }), (availability === null || availability === void 0 ? void 0 : availability.daysOfWeek.length) && /*#__PURE__*/React.createElement(ContactItem, {
    "data-testid": TEST_ID.SUPPORT_TAB_SUPPORT_TIMINGS
  }, humanizeSupportTimings(availability)), phoneNumber && /*#__PURE__*/React.createElement(ContactItem, {
    "data-testid": TEST_ID.SUPPORT_TAB_PHONE_NUMBER
  }, phoneNumber), emailId && /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(ExternalLink, {
    href: makeMailToLink(emailId),
    title: "Contact via email",
    "data-testid": TEST_ID.SUPPORT_TAB_EMAIL_ID,
    onClick: () => props.onSupportLinkClick(SupportResourcesLink.EMAIL)
  }, "Email vendor"))), /*#__PURE__*/React.createElement(SupportRequest, {
    supportTicketSystemUrl: supportTicketSystemUrl,
    issueTrackerUrl: issueTrackerUrl,
    websiteUrl: websiteUrl,
    onSupportLinkClick: props.onSupportLinkClick
  }), statusPageUrl && /*#__PURE__*/React.createElement(ContactItem, null, /*#__PURE__*/React.createElement(ExternalLink, {
    href: statusPageUrl,
    title: "Visit status page",
    "data-testid": TEST_ID.SUPPORT_TAB_STATUS_PAGE_URL,
    onClick: () => props.onSupportLinkClick(SupportResourcesLink.STATUS_PAGE)
  }, "Go to vendor status page"))));
};
export default SupportContact;